import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Modal, Space, Table, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { getAdminUserInfo } from "../auth/auth.storage";
import {
  addCoins,
  createUserByAdmin,
  createUserBySuperAdmin,
  deleteUser,
  getUsers,
  subtractCoins,
  updateUser,
} from "../sources/admin.source";

type Row = {
  _id: string;
  username: string;
  phone: string;
  role: string;
  coins: number;
};

const { Title, Text } = Typography;

const UsersPage: React.FC = () => {
  const me = getAdminUserInfo();
  const role = me?.role ?? "USER";

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCoins, setOpenCoins] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [coinsUserId, setCoinsUserId] = useState<string>("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getUsers(role);
      const sorted = [...data].sort((a: Row, b: Row) => b._id.localeCompare(a._id));
      setRows(sorted);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Không tải được danh sách user";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.username?.toLowerCase().includes(s));
  }, [q, rows]);

  const columns: ColumnsType<Row> = [
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Role", dataIndex: "role", key: "role", width: 140 },
    { title: "Coins", dataIndex: "coins", key: "coins", width: 140 },
    {
      title: "Action",
      key: "action",
      width: 220,
      render: (_, r) => (
        <Space>
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setCoinsUserId(r._id);
              setOpenCoins(true);
            }}
          >
            Coins
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(r);
              setOpenEdit(true);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "Xoá người dùng",
                content: `Bạn có chắc muốn xoá ${r.username} không?`,
                okText: "Xoá",
                cancelText: "Huỷ",
                async onOk() {
                  await deleteUser(r._id);
                  message.success("Đã xoá");
                  await load();
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <Title level={3} style={{ margin: 0, color: "#111114" }}>
          Quản lý User
        </Title>
        <Text style={{ color: "rgba(17,17,20,0.65)" }}>
          {role === "ADMIN"
            ? "Admin chỉ xem user thuộc quyền quản lý."
            : "Superadmin xem toàn bộ user."}
        </Text>
      </div>

      <Card>
        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
          <Input.Search
            placeholder="Tìm theo username..."
            allowClear
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 380 }}
          />
          <Button type="primary" onClick={() => setOpenCreate(true)}>
            Tạo user
          </Button>
        </div>
      </Card>

      <Card>
        <Table<Row>
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <UserModal
        mode="create"
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onDone={async () => {
          setOpenCreate(false);
          await load();
        }}
      />

      <UserModal
        mode="edit"
        open={openEdit}
        editing={editing}
        onClose={() => {
          setOpenEdit(false);
          setEditing(null);
        }}
        onDone={async () => {
          setOpenEdit(false);
          setEditing(null);
          await load();
        }}
      />

      <CoinsModal
        open={openCoins}
        userId={coinsUserId}
        onClose={() => {
          setOpenCoins(false);
          setCoinsUserId("");
        }}
        onDone={async () => {
          setOpenCoins(false);
          setCoinsUserId("");
          await load();
        }}
      />
    </div>
  );
};

const UserModal: React.FC<{
  open: boolean;
  mode: "create" | "edit";
  editing?: Row | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ open, mode, editing, onClose, onDone }) => {
  const me = getAdminUserInfo();
  const role = me?.role ?? "USER";

  const [username, setUsername] = useState(editing?.username ?? "");
  const [phone, setPhone] = useState(editing?.phone ?? "");
  const [password, setPassword] = useState("");
  const [rolePick, setRolePick] = useState<"ADMIN" | "USER">("USER");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && editing) {
      setUsername(editing.username ?? "");
      setPhone(editing.phone ?? "");
      setPassword("");
    }
    if (mode === "create") {
      setUsername("");
      setPhone("");
      setPassword("");
      setRolePick("USER");
    }
  }, [mode, editing, open]);

  const submit = async () => {
    if (!username.trim() || !phone.trim() || (mode === "create" && !password.trim())) {
      message.error("Vui lòng nhập đủ thông tin");
      return;
    }
    setLoading(true);
    try {
      if (mode === "create") {
        if (role === "SUPERADMIN") {
          await createUserBySuperAdmin({
            username: username.trim(),
            phone: phone.trim(),
            password: password.trim(),
            role: rolePick,
          });
        } else if (role === "ADMIN") {
          await createUserByAdmin({
            username: username.trim(),
            phone: phone.trim(),
            password: password.trim(),
            managedByUsername: me?.userName ?? "",
          });
        } else {
          message.error("Không có quyền");
          return;
        }
        message.success("Đã tạo user");
      } else {
        if (!editing?._id) return;
        await updateUser(editing._id, {
          username: username.trim(),
          phone: phone.trim(),
          password: password.trim() ? password.trim() : undefined,
        });
        message.success("Đã cập nhật");
      }
      onDone();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Thao tác thất bại";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "create" ? "Tạo user" : "Cập nhật user"}
      onCancel={onClose}
      onOk={submit}
      okButtonProps={{ loading }}
      okText={mode === "create" ? "Tạo" : "Lưu"}
      cancelText="Đóng"
    >
      <div style={{ display: "grid", gap: 12 }}>
        <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
        <Input.Password value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "create" ? "Password" : "Password (để trống nếu không đổi)"} />
        {role === "SUPERADMIN" && mode === "create" && (
          <div style={{ display: "flex", gap: 10 }}>
            <Button type={rolePick === "USER" ? "primary" : "default"} onClick={() => setRolePick("USER")}>
              USER
            </Button>
            <Button type={rolePick === "ADMIN" ? "primary" : "default"} onClick={() => setRolePick("ADMIN")}>
              ADMIN
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const CoinsModal: React.FC<{
  open: boolean;
  userId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ open, userId, onClose, onDone }) => {
  const [amount, setAmount] = useState<string>("0");
  const [mode, setMode] = useState<"add" | "sub">("add");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("0");
      setMode("add");
    }
  }, [open]);

  const submit = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n === 0) {
      message.error("Số xu không hợp lệ");
      return;
    }
    setLoading(true);
    try {
      if (mode === "add") await addCoins(userId, n);
      else await subtractCoins(userId, n);
      message.success("Đã cập nhật xu");
      onDone();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Cập nhật xu thất bại";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Thêm / Trừ xu"
      onCancel={onClose}
      onOk={submit}
      okButtonProps={{ loading }}
      okText="Lưu"
      cancelText="Đóng"
    >
      <div style={{ display: "grid", gap: 12 }}>
        <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Số xu" />
        <div style={{ display: "flex", gap: 10 }}>
          <Button type={mode === "add" ? "primary" : "default"} onClick={() => setMode("add")}>
            Thêm xu
          </Button>
          <Button type={mode === "sub" ? "primary" : "default"} onClick={() => setMode("sub")}>
            Trừ xu
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UsersPage;

